import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Features from "./components/Features";
import DocumentProcessor from "./components/DocumentProcessor";
import Pricing from "./components/Pricing";
import Footer from "./components/Footer";
import SignInModal from "./components/auth/SignInModal";
import SignUpModal from "./components/auth/SignUpModal";
import AuthCallback from "./components/auth/AuthCallback";
import LegalModalManager from "./components/legal/LegalModalManager";
import { Toaster } from "./components/ui/toaster";
import { LegalDocumentGuideWithAd, LegalTechReportWithAd } from "./components/ads/AdSenseAd";
import AffiliateSection from "./components/marketing/AffiliateSection";
import LegalSuiteSection from "./components/marketing/LegalSuiteSection";
import LegalBlog from "./components/content/LegalBlog";
import BlogArticle from "./components/content/BlogArticle";
import TermsOfService from "./components/legal/TermsOfService";
import PrivacyPolicy from "./components/legal/PrivacyPolicy";
import CookiePolicy from "./components/legal/CookiePolicy";
import SecurityPage from "./components/legal/SecurityPage";
import FeaturesPage from "./components/pages/FeaturesPage";
import PricingPage from "./components/pages/PricingPage";
import AboutPage from "./components/pages/AboutPage";
import ContactPage from "./components/pages/ContactPage";
import DashboardPage from "./components/pages/DashboardPage";
import FAQPage from "./components/pages/FAQPage";
import HowItWorksPage from "./components/pages/HowItWorksPage";
import CostSavingsCalculator from "./components/CostSavingsCalculator";
import Testimonials from "./components/Testimonials";

const Home = () => {
  return (
    <>
      <Header />
      <Hero />
      <Features />
      <CostSavingsCalculator />
      <Testimonials />
      <DocumentProcessor />
      {/* Our 3-step workflow section */}
      <AffiliateSection />
      <Pricing />
      <Footer />
    </>
  );
};

const BlogPage = () => {
  return (
    <>
      <Header />
      <LegalBlog />
      <Footer />
    </>
  );
};

const GuidesPage = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Legal Document Resources & Guides
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive guides and expert resources for legal document management, 
              conversion, and technology implementation.
            </p>
          </div>
          
          {/* Content with Ad Integration */}
          <LegalDocumentGuideWithAd />
          <LegalTechReportWithAd />
        </div>
      </div>
      <Footer />
    </>
  );
};

// Legal Pages Wrappers
const TermsPage = () => (
  <>
    <Header />
    <TermsOfService />
    <Footer />
  </>
);

const PrivacyPage = () => (
  <>
    <Header />
    <PrivacyPolicy />
    <Footer />
  </>
);

const CookiePage = () => (
  <>
    <Header />
    <CookiePolicy />
    <Footer />
  </>
);

const SecurityPageWrapper = () => (
  <>
    <Header />
    <SecurityPage />
    <Footer />
  </>
);

// AppRouter component to handle OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - CRITICAL for OAuth flow
  // This must be checked synchronously during render, NOT in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:id" element={<BlogArticle />} />
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiePage />} />
      <Route path="/security" element={<SecurityPageWrapper />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-white dark:bg-slate-950 transition-colors duration-300">
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <SignInModal />
          <SignUpModal />
          <LegalModalManager />
          <Toaster />
        </AuthProvider>
      </div>
    </ThemeProvider>
  );
}

export default App;